/**
 * Token Service
 * Centralized service for handling authentication tokens
 */

const TOKEN_KEY = 'token';

// Get token from all possible storage locations
const getToken = () => {
  // Try session storage first (most common)
  const sessionToken = sessionStorage.getItem(TOKEN_KEY);
  if (sessionToken) return sessionToken;
  
  // Try local storage directly
  const localToken = localStorage.getItem(TOKEN_KEY);
  if (localToken) return localToken;
  
  // Try Redux state in local storage
  try {
    const state = localStorage.getItem('state');
    if (state) {
      const parsedState = JSON.parse(state);
      const stateToken = parsedState?.auth?.token || parsedState?.auth?.user?.token;
      if (stateToken) return stateToken;
    }
  } catch (error) {
    console.error('Error parsing state from localStorage:', error);
  }
  
  return null;
};

// Save token to all storage locations for redundancy
const setToken = (token) => {
  if (!token) {
    // If token is null/undefined/empty, clear all tokens
    removeToken();
    return;
  }
  
  // Store in session storage (primary location)
  sessionStorage.setItem(TOKEN_KEY, token);
  
  // Also store in localStorage for persistence
  localStorage.setItem(TOKEN_KEY, token);
  
  // Update Redux state in localStorage if it exists
  try {
    const state = localStorage.getItem('state');
    if (state) {
      const parsedState = JSON.parse(state);
      const updatedState = {
        ...parsedState,
        auth: {
          ...parsedState.auth,
          isAuthenticated: true,
          token: token,
          user: {
            ...parsedState?.auth?.user,
            token: token
          }
        }
      };
      localStorage.setItem('state', JSON.stringify(updatedState));
    }
  } catch (error) {
    console.error('Error updating token in state:', error);
  }
  
  console.log('Token saved to all storage locations');
};

// Remove token from all storage locations
const removeToken = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  
  // Update Redux state in localStorage if it exists
  try {
    const state = localStorage.getItem('state');
    if (state) {
      const parsedState = JSON.parse(state);
      const updatedState = {
        ...parsedState,
        auth: {
          ...parsedState.auth,
          isAuthenticated: false,
          token: null,
          user: {
            ...parsedState?.auth?.user,
            token: null
          }
        }
      };
      localStorage.setItem('state', JSON.stringify(updatedState));
    }
  } catch (error) {
    console.error('Error removing token from state:', error);
  }
  
  console.log('Token removed from all storage locations');
};

// Check if user is authenticated
const isAuthenticated = () => {
  return !!getToken();
};

// Debug method to log all token storage locations
const debugTokenStorage = () => {
  const sessionToken = sessionStorage.getItem(TOKEN_KEY);
  const localToken = localStorage.getItem(TOKEN_KEY);
  
  let stateToken = null;
  try {
    const state = localStorage.getItem('state');
    if (state) {
      const parsedState = JSON.parse(state);
      stateToken = parsedState?.auth?.token || parsedState?.auth?.user?.token;
    }
  } catch (error) {
    console.error('Error parsing state for debug:', error);
  }
  
  console.log('Token Storage Debug:');
  console.log('- Session Storage:', sessionToken ? `${sessionToken.substring(0, 10)}...` : 'null');
  console.log('- Local Storage:', localToken ? `${localToken.substring(0, 10)}...` : 'null');
  console.log('- Redux State:', stateToken ? `${stateToken.substring(0, 10)}...` : 'null');
  console.log('- Is Authenticated:', !!getToken());
  
  return {
    sessionToken: !!sessionToken,
    localToken: !!localToken,
    stateToken: !!stateToken,
    isAuthenticated: !!getToken()
  };
};

export default {
  getToken,
  setToken,
  removeToken,
  isAuthenticated,
  debugTokenStorage
};
